import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, numberAttribute, output } from '@angular/core';
import { IconComponent, TIcon } from '@app/components/_ui/icon';
import { TColors } from '@app/interfaces/root-type.interface';
import { AppStyles } from '@app/services/app-styles.service';
import { TranslateModule } from '@ngx-translate/core';
import { translations } from '@utils/translations';
import { BehaviorSubject, interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'custom-alert',
    template: `
        @if (visible()) {
            <div
                class="alert-balloon"
                [style.background]="background() ? appStyles.getColor(background()!) : ''"
                [style.color]="appStyles.getColor(color())"
                [class.p-xs]="!!background()"
                [class.rounded-item]="!!background()"
                @errorItemAnimation>
                @if (icon()) {
                    <icon
                        [name]="icon()!"
                        [color]="iconColor() ?? color()"
                        class="mr-1.5"
                        size="18" />
                }
                <div class="whitespace-pre-line text-start">{{ message() | translate }}</div>
                <ng-content></ng-content>
            </div>
            @if (expandedErrorMessage()) {
                <div
                    class="pt-0"
                    [style.background]="background() ? appStyles.getColor(background()!) : ''"
                    [style.color]="appStyles.getColor(color())"
                    [class.p-xs]="!!background()"
                    [class.rounded-item]="!!background()"
                    [@expandMessage]="expanded() ? 'expanded' : 'collapsed'">
                    <div class="flex flex-col ml-7 max-h-48 overflow-y-auto">
                        {{ translations.global.message_details | translate }} ({{
                            expandedErrorType()
                        }}):
                        <ul class="list-['-_'] ml-4">
                            <li>{{ expandedErrorMessage() }}</li>
                        </ul>
                    </div>
                </div>
            }
        }
    `,
    styles: `
        :host {
            display: block;

            .alert-balloon {
                /*margin-block: var(--margin-sm);*/
                text-align: center;
                text-wrap: balance;
                display: flex;
                flex-direction: row;
                align-items: center;
                width: 100%;
            }
        }
    `,
    animations: [
        trigger('errorItemAnimation', [
            transition(':enter', [
                style({
                    opacity: 0,
                    height: '0px',
                    minHeight: '0',
                    margin: '0',
                    padding: '0',
                }),
                animate(
                    '200ms',
                    style({
                        opacity: 1,
                        height: '*',
                        minHeight: '*',
                        margin: '*',
                        padding: '*',
                    }),
                ),
            ]),
            transition(':leave', [
                style({ opacity: 1, height: '*', minHeight: '*', margin: '*', padding: '*' }),
                animate(
                    '200ms',
                    style({
                        opacity: 0,
                        height: '0px',
                        minHeight: '0',
                        margin: '0',
                        padding: '0',
                    }),
                ),
            ]),
        ]),
        trigger('expandMessage', [
            state('collapsed', style({ height: '0', opacity: '0' })),
            state('expanded', style({ height: '*', opacity: '1' })),
            transition('collapsed <=> expanded', animate('300ms ease-in-out')),
        ]),
    ],
    imports: [CommonModule, TranslateModule, IconComponent],
})
export class CustomAlertComponent {
    appStyles: AppStyles = inject(AppStyles);

    color = input.required<TColors>();
    message = input.required<string>();
    messagePlus = input<string>();
    icon = input<TIcon>();
    iconColor = input<TColors>();
    background = input<TColors>();
    autoClose = input<number, unknown>(0, { transform: numberAttribute });
    expandedErrorMessage = input<string>();
    expandedErrorType = input<string>();
    expanded = input(false);

    alertClosed = output<void>();

    countdown$: Subject<void> = new Subject<void>();
    remainingSeconds$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    visible = computed(() => !!this.message());

    constructor() {
        effect(() => {
            if (this.visible() && this.autoClose() > 0) this.startTimer();
            else this.stopTimer();
        });
    }

    startTimer() {
        this.remainingSeconds$.next(this.autoClose());
        interval(1000)
            .pipe(takeUntil(this.countdown$))
            .subscribe(() => {
                this.remainingSeconds$.next(this.remainingSeconds$.getValue() - 1);

                if (this.remainingSeconds$.getValue() === 0) {
                    this.alertClosed.emit();
                    this.stopTimer();
                }
            });
    }

    stopTimer(): void {
        this.countdown$.next();
    }

    protected readonly translations = translations;
}
